import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const steps = [
  {
    number: "01",
    title: "Create Your Account",
    description:
      "Head to edufocalacademy.com and click Get Started. Fill in your child's name, grade (4, 5 or 6), and your parent contact details including your WhatsApp number for weekly progress reports.",
    color: "bg-[#0789c3]",
    lightColor: "bg-[#0789c3]/10",
    textColor: "text-[#0789c3]",
    borderColor: "border-[#0789c3]/20",
  },
  {
    number: "02",
    title: "Choose Your Plan",
    description:
      "Select Monthly ($2,500 JMD/month) or Annual ($10,000 JMD/year, saving $20,000 JMD). Both plans give your child full access to everything on the platform.",
    color: "bg-[#854099]",
    lightColor: "bg-[#854099]/10",
    textColor: "text-[#854099]",
    borderColor: "border-[#854099]/20",
  },
  {
    number: "03",
    title: "Complete Payment",
    description:
      "Pay securely online using your credit or debit card. Your subscription activates instantly with no waiting and no delays. You're ready to go the moment payment goes through.",
    color: "bg-[#0789c3]",
    lightColor: "bg-[#0789c3]/10",
    textColor: "text-[#0789c3]",
    borderColor: "border-[#0789c3]/20",
  },
  {
    number: "04",
    title: "Log In and Explore",
    description:
      "Once payment is confirmed, log straight in. Your child's dashboard is ready to go with quizzes, their leaderboard ranking and today's recommended practice.",
    color: "bg-[#854099]",
    lightColor: "bg-[#854099]/10",
    textColor: "text-[#854099]",
    borderColor: "border-[#854099]/20",
  },
  {
    number: "05",
    title: "Start Practising",
    description:
      "Pick a subject and start. The platform will guide your child through questions suited to their level, tracking their progress with every answer and flagging areas that need more attention.",
    color: "bg-[#0789c3]",
    lightColor: "bg-[#0789c3]/10",
    textColor: "text-[#0789c3]",
    borderColor: "border-[#0789c3]/20",
  },
  {
    number: "06",
    title: "Track Progress Together",
    description:
      "Check your child's progress report anytime from the dashboard. Annual plan subscribers receive a weekly summary straight to WhatsApp every week, so you're always in the loop.",
    color: "bg-[#854099]",
    lightColor: "bg-[#854099]/10",
    textColor: "text-[#854099]",
    borderColor: "border-[#854099]/20",
  },
];

const faqs = [
  {
    question: "What grades does EduFocal cover?",
    answer:
      "EduFocal Academy covers Grades 4, 5 and 6, fully aligned to Jamaica's PEP (Primary Exit Profile) curriculum. All practice questions, reading materials and progress tracking are mapped to what students are tested on.",
  },
  {
    question: "Can my child use EduFocal on a phone?",
    answer:
      "Yes. EduFocal works on any device with a browser: phone, tablet or computer. There is no app to download. Your child simply opens the browser, logs in and starts studying. Their progress is saved automatically across all devices.",
  },
  {
    question: "What subjects are covered?",
    answer:
      "EduFocal covers all four PEP subjects: Maths, English Language, Science and Social Studies. Each subject has a dedicated question bank with hundreds of practice questions ranging from straightforward recall to higher-order thinking.",
  },
  {
    question: "Can I cancel my subscription?",
    answer:
      "Yes. You can cancel anytime from your account settings. Monthly subscribers will not be charged again after cancellation and retain access until the end of their current billing period. Annual subscribers retain full access until the end of their annual billing period.",
  },
  {
    question: "How do the WhatsApp reports work?",
    answer:
      "Annual plan subscribers receive a weekly progress summary sent directly to the parent WhatsApp number provided during sign-up. The report covers your child's activity, subjects practised, XP earned and areas that need attention, all without needing to log in.",
  },
];

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0789c3] via-[#0789c3] to-[#065f88] py-20 md:py-28">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#854099]/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#7fc4e1]/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative px-4 md:px-6 max-w-4xl mx-auto text-center">
        <p className="font-[Montserrat] text-sm font-semibold uppercase tracking-widest text-white/70 mb-4">
          How It Works
        </p>
        <h1 className="font-[Montserrat] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-white mb-6">
          Getting Started is{" "}
          <span className="text-[#FFC209]">Simple</span>
        </h1>
        <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed">
          From sign-up to your first quiz in under 5 minutes.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/signup">
            <Button
              size="lg"
              className="text-lg px-10 py-7 rounded-full bg-[#FFC209] text-gray-900 hover:bg-[#e6af08] shadow-xl hover:shadow-2xl transition-all min-h-14 font-[Montserrat] font-bold tracking-wide"
            >
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function StepsSection() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container px-4 md:px-6 max-w-5xl mx-auto">
        <div className="text-center mb-14 md:mb-16">
          <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
            Step by Step
          </p>
          <h2 className="font-[Montserrat] text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
            Your Path to PEP Readiness
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Follow these six simple steps to get your child set up and studying on EduFocal Academy.
          </p>
        </div>

        <div className="relative">
          {/* Vertical connector line for desktop */}
          <div className="hidden md:block absolute left-[2.75rem] top-10 bottom-10 w-0.5 bg-gradient-to-b from-[#0789c3]/30 via-[#854099]/30 to-[#0789c3]/30"></div>

          <div className="space-y-8 md:space-y-10">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col md:flex-row gap-5 md:gap-8 items-start">
                {/* Step number circle */}
                <div className="flex-shrink-0 flex flex-row md:flex-col items-center gap-4 md:gap-0">
                  <div
                    className={`w-[5.5rem] h-[5.5rem] ${step.color} rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}
                  >
                    <span className="font-[Montserrat] font-bold text-2xl text-white">
                      {step.number}
                    </span>
                  </div>
                </div>

                {/* Step content */}
                <div
                  className={`flex-1 bg-white border ${step.borderColor} rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow`}
                >
                  <h3 className={`font-[Montserrat] text-xl font-bold mb-3 ${step.textColor}`}>
                    {step.title}
                  </h3>
                  <p className="font-[Inter] text-gray-500 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick summary strip */}
        <div className="mt-16 md:mt-20 bg-[#EBEFF4]/60 rounded-2xl p-6 md:p-8 text-center">
          <p className="font-[Montserrat] font-bold text-gray-800 text-lg mb-2">
            That's it. Your child is ready to study.
          </p>
          <p className="text-gray-500 text-sm max-w-lg mx-auto">
            The whole process takes under five minutes. Once you're in, the platform does the rest, guiding your child through questions, tracking progress and keeping them motivated every day.
          </p>
          <div className="mt-6">
            <Link href="/signup">
              <Button
                size="lg"
                className="rounded-full bg-[#0789c3] hover:bg-[#066d9c] text-white font-[Montserrat] font-bold px-8 py-6 shadow-md hover:shadow-lg transition-all"
              >
                Create Your Account
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between text-left px-6 py-5 md:px-7 md:py-6 bg-white hover:bg-[#EBEFF4]/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0789c3]"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-[Montserrat] font-semibold text-gray-900 text-base md:text-lg pr-4">
          {question}
        </span>
        <span
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 border-gray-200 transition-transform duration-200 ${
            open ? "rotate-45 border-[#0789c3] bg-[#0789c3]/10" : ""
          }`}
        >
          <svg
            className={`w-4 h-4 ${open ? "text-[#0789c3]" : "text-gray-400"}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6 md:px-7 md:pb-7 bg-white border-t border-gray-100">
          <p className="font-[Inter] text-gray-500 leading-relaxed pt-4">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

function FaqSection() {
  return (
    <section className="py-20 md:py-28 bg-[#EBEFF4]/40">
      <div className="container px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
            Frequently Asked Questions
          </p>
          <h2 className="font-[Montserrat] text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
            Common Questions
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Have a question that isn't answered here? Send us a message and we'll get back to you quickly.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FaqItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm mb-4">Still have questions?</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-[#0789c3] hover:bg-[#066d9c] text-white font-[Montserrat] font-semibold px-8 py-3.5 rounded-full transition-colors shadow-lg shadow-[#0789c3]/30 text-sm"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-[#854099] to-[#0789c3] relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#FFC209]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative px-4 md:px-6 max-w-4xl mx-auto text-center">
        <h2 className="font-[Montserrat] text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
          Ready to Get Started?
        </h2>
        <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
          Join thousands of Jamaican families preparing their children for PEP success. It takes less than five minutes to set up and your child can start practising today.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/signup">
            <Button
              size="lg"
              className="text-lg px-10 py-7 rounded-full bg-[#FFC209] text-gray-900 hover:bg-[#e6af08] shadow-xl hover:shadow-2xl transition-all min-h-14 font-[Montserrat] font-bold"
            >
              Get Started
            </Button>
          </Link>
          <Button
            size="lg"
            variant="outline"
            className="text-lg px-10 py-7 rounded-full border-2 border-white/30 text-white hover:bg-white/10 min-h-14 font-[Montserrat] font-semibold bg-transparent"
            onClick={() => window.location.href = '/contact'}
          >
            Text Us
          </Button>
        </div>
        <p className="text-white/50 text-sm mt-6">Cancel anytime. No long-term commitment required.</p>
      </div>
    </section>
  );
}

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <HeroSection />
        <StepsSection />
        <FaqSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
